import { Injectable, inject } from '@angular/core';
import { BackendService } from './backend.service';
import { Observable } from 'rxjs';
import {
	AddUserResponse,
	User,
	UserResponse,
} from 'src/shared/models/user.model';
import { environment } from 'src/environments/environment';
import { BaseResponse } from 'src/shared/models/base-response.model';
import { map } from 'rxjs/operators';
import {
	Appointment,
	AppointmentResponse,
} from 'src/shared/models/appointment.model';
import { UserPassport } from 'src/shared/models/user-passport.model';
import { Housing } from 'src/shared/models/housing.model';
import { UserPhoneInfo } from 'src/shared/models/user-phone-info.model';
import { Dependent } from 'src/shared/models/dependent.model';

const API_URL = `${environment.apiUrl}/web/basic`;

export const ADD_URL = `${API_URL}/add`;
export const ADD_RESPONSE = 'Added User';

export const ARCHIVE_USER_URL = `${API_URL}/archiveuser`;
export const ARCHIVE_USER_RESPONSE = 'Archived User';

export const ACTIVATE_USER_URL = `${API_URL}/activateuser`;
export const ACTIVATE_USER_RESPONSE = 'Activated User';

export const CURRENT_APPOINTMENT_URL = `${API_URL}/currentappointment`;
export const CURRENT_APPOINTMENT_RESPONSE = 'Fetched Current Appointment';

export const SAVE_USER_GENERAL_INFO_URL = `${API_URL}/saveusergeneralinfo`;
export const SAVE_USER_GENERAL_INFO_RESPONSE = 'Saved User General';

export const SAVE_USER_HOUSING_INFO_URL = `${API_URL}/saveuserhousinginfo`;
export const SAVE_USER_HOUSING_INFO_RESPONSE = 'Saved User Housing Info';

export const SAVE_USER_DEPENDENTS_URL = `${API_URL}/saveuserdependents`;
export const SAVE_USER_DEPENDENTS_RESPONSE = 'Saved User Dependents';

@Injectable({
	providedIn: 'root',
})
export class BasicBackendService {
	private backendService = inject(BackendService);

	/**
	 * Add Basic User
	 * ===============
	 * API Endpoint: /web/basic/add
	 * JSON Parameters: netId, employeeId, firstName, lastName
	 *
	 * Adds a new User and returns an updated User object
	 */
	addUser(user: User): Observable<AddUserResponse> {
		return this.backendService
			.post(ADD_URL, ADD_RESPONSE, {
				netId: user.netId,
				employeeId: user.employeeId,
				firstName: user.firstName,
				lastName: user.lastName,
			})
			.pipe(map((response: AddUserResponse) => response));
	}

	/**
	 * Archive User
	 * =============
	 * API Endpoint: /web/basic/archiveuser
	 * JSON Parameters: id
	 *
	 * Archives the user and returns a the updated User object
	 */
	archiveUser(userId: number): Observable<User> {
		return this.backendService
			.post(ARCHIVE_USER_URL, ARCHIVE_USER_RESPONSE, {
				id: userId,
			})
			.pipe(
				map((response: UserResponse) => User.fromJSON(response.user))
			);
	}

	/**
	 * Activate User
	 * ==============
	 * API Endpoint: /web/basic/activateuser
	 * JSON Parameters: id
	 *
	 * Activates the user and returns a the updated User object
	 */
	activateUser(userId: number): Observable<User> {
		return this.backendService
			.post(ACTIVATE_USER_URL, ACTIVATE_USER_RESPONSE, { id: userId })
			.pipe(
				map((response: UserResponse) => User.fromJSON(response.user))
			);
	}

	/**
	 * Current Appointment
	 * ====================
	 * API Endpoint: /web/basic/currentappointment
	 * JSON Parameters: id
	 *
	 * Returns the current/primary appointment for the given userId
	 */
	currentAppointment(userId: number): Observable<Appointment> {
		return this.backendService
			.post(CURRENT_APPOINTMENT_URL, CURRENT_APPOINTMENT_RESPONSE, {
				id: userId,
			})
			.pipe(
				map((response: AppointmentResponse) =>
					response.appointments && response.appointments.length > 0
						? Appointment.fromJSON(response.appointments[0])
						: null
				)
			);
	}

	/**
	 * Save User General
	 * ==================
	 * API Endpoint: /web/basic/saveusergeneralinfo
	 * JSON Parameters: user: User, appointment: Appointment,
	 * 	passports: UserPassport[], phoneNumbers: UserPhoneInfo[]
	 *
	 * Saves the General part of the form
	 */
	saveUserGeneralInfo(
		user: User,
		appointment: Appointment,
		passports: UserPassport[],
		phoneNumbers: UserPhoneInfo[]
	): Observable<UserResponse> {
		return this.backendService
			.post(SAVE_USER_GENERAL_INFO_URL, SAVE_USER_GENERAL_INFO_RESPONSE, {
				user: user,
				appointment: appointment,
				passports: passports,
				phoneNumbers: phoneNumbers,
			})
			.pipe(map((response: UserResponse) => response));
	}

	/**
	 * Save User Housing Info
	 * =======================
	 * API Endpoint: /web/basic/saveuserhousinginfo
	 * JSON Parameters: id, housing: HousingInfo
	 *
	 * Saves the User Housing Info part of the form
	 */
	saveUserHousingInfo(
		userId: number,
		housing: Housing
	): Observable<BaseResponse> {
		return this.backendService.post(
			SAVE_USER_HOUSING_INFO_URL,
			SAVE_USER_HOUSING_INFO_RESPONSE,
			{ id: userId, housing: housing }
		);
	}

	/**
	 * Save User Dependents
	 * =========================
	 * API Endpoint: /web/hr/saveuserdependents
	 * JSON Parameters: id, dependents: Dependent[]
	 *
	 * Saves the User Dependents part of the form and returns an updated Dependents
	 */
	saveUserDependents(
		userId: number,
		dependents: Dependent[]
	): Observable<BaseResponse> {
		return this.backendService.post(
			SAVE_USER_DEPENDENTS_URL,
			SAVE_USER_DEPENDENTS_RESPONSE,
			{
				id: userId,
				dependents: dependents,
			}
		);
	}
}
